import React from 'react';
import {
  Button,
  Checkbox,
  Input,
  Message,
  SelectPicker,
  Table,
  useToaster,
} from 'rsuite';
import * as trainersApi from '../../api/trainers';
import { SortType } from 'rsuite-table';
import EditIcon from '@rsuite/icons/Edit';
import DeleteIcon from '@rsuite/icons/Trash';
import UploadImageModal from '../../common/UploadImageModal';
import { useConfirm } from '../../common/useConfirm';
import { API_DOMAIN } from '../../config';
import UploadImages from './UploadImages';
import CreateSectionModal from './CreateSectionModal';
import { createSection, getSections } from '../../api/gallery';
import * as galleryApi from '../../api/gallery';
import * as coursesApi from '../../api/courses';
import { FormField } from '../../common/FormModal';
import LanguageTabs, {
  getLanguagePanelId,
  getLanguageTabId,
  LanguageKey,
} from '../../common/LanguageTabs';
import TablePagination from '../../common/TablePagination';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import PageLayout from '../../common/PageLayout';
import EmptyState from '../../common/EmptyState';

const { Column, HeaderCell, Cell } = Table;

export default function Gallery() {
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();

  const [state, setState] = React.useState<{
    page: number;
    totalPages: number;
    totalCount: number;
    data: {
      id: string;
      image?: string;
      sectionId?: string;
      createdAt?: number;
    }[];
  }>({
    page: 0,
    totalPages: 0,
    totalCount: 0,
    data: [],
  });
  const [perPage, setPerPage] = React.useState(15);
  const [sections, setSections] = React.useState<
    {
      id: string;
      name: string;
      nameEn: string;
      nameDe: string;
      active: boolean;
      createdAt: number;
      updatedAt: number;
    }[]
  >([]);

  const [loading, setLoading] = React.useState(false);
  const [sectionsData, setSectionsData] = React.useState<
    {
      value: string;
      label: string;
    }[]
  >([]);
  const [selectedSection, setSelectedSection] = React.useState<{
    id: string;
    name: string;
    nameEn: string;
    nameDe: string;
    active: boolean;
    createdAt: number;
    updatedAt: number;
  } | null>(null);
  const [sectionToEdit, setSectionToEdit] = React.useState<{
    id?: string;
    name?: string;
    nameEn?: string;
    nameDe?: string;
    active?: boolean;
    createdAt?: number;
    updatedAt?: number;
  } | null>(null);
  // const [addToQueueCourseId, setAddToQueueCourseId] = React.useState<string>('');
  const [updateModalObject, setUpdateModalObject] = React.useState<any | null>(
    null
  );
  const [createModalOpen, setCreateModalOpen] = React.useState<boolean>(false);
  const [updateTrainerImageObj, setUpdateTrainerImageObj] =
    React.useState<null | {
      id?: string;
      image?: string;
    }>(null);
  const [displayEditSection, setDisplayEditSection] =
    React.useState<boolean>(false);
  const [activeLanguage, setActiveLanguage] = React.useState<LanguageKey>('ua');
  const languageTabPrefix = 'gallery-section-language';

  const loadSections = async () => {
    const sections = await galleryApi.getSections();
    setSections(sections.sections);
    setSectionsData(
      sections.sections.map(s => ({ value: s.id, label: s.name }))
    );
  };
  const clickedCellRef = React.useRef<boolean>(false);

  const getData = async (page: number = 1) => {
    if (!selectedSection) {
      return;
    }
    setLoading(true);
    const response = await galleryApi.getSectionImages(
      selectedSection.id,
      page,
      perPage
    );
    setState(response);
    setLoading(false);
  };

  React.useEffect(() => {
    loadSections().catch(console.error);
  }, []);

  React.useEffect(() => {
    if (selectedSection) {
      getData();
    }
  }, [selectedSection]);

  React.useEffect(() => {
    if (displayEditSection) {
      setActiveLanguage('ua');
    }
  }, [displayEditSection, selectedSection?.id]);

  return (
    <>
      {ConfirmDialog}
      <PageLayout
        title="Галерея"
        count={sections.length || undefined}
        subtitle="Керуйте секціями галереї та зображеннями в них"
        actions={
          <>
            <SelectPicker
              data={sectionsData}
              value={selectedSection?.id || null}
              placeholder="Оберіть секцію"
              style={{ minWidth: 220 }}
              onSelect={value => {
                const section = sections.find(s => s.id === value);
                setSelectedSection(section || null);
                if (section) {
                  setSectionToEdit(JSON.parse(JSON.stringify(section || null)));
                }
              }}
            />
            <Button
              appearance="primary"
              onClick={() => {
                setCreateModalOpen(true);
              }}
            >
              Створити секцію
            </Button>
          </>
        }
      >
        {!selectedSection && (
          <EmptyState
            illustration="gallery"
            title={
              sections.length === 0
                ? 'У галереї поки немає секцій'
                : 'Оберіть секцію зліва'
            }
            description={
              sections.length === 0
                ? 'Створіть першу секцію, щоб почати додавати зображення.'
                : 'Виберіть секцію з випадаючого списку вгорі, щоб переглянути та керувати її зображеннями.'
            }
            action={
              sections.length === 0 ? (
                <Button
                  appearance="primary"
                  onClick={() => setCreateModalOpen(true)}
                >
                  Створити секцію
                </Button>
              ) : null
            }
          />
        )}
        <div style={{ display: selectedSection ? 'block' : 'none' }}>
          <div>
            {selectedSection && (
              <>
                <div>
                  <Button
                    onClick={() => {
                      setDisplayEditSection(!displayEditSection);
                    }}
                  >
                    Редагувати
                  </Button>
                  <Button
                    onClick={async () => {
                      const result = await confirm({
                        message: `Ви дійсно хочете видалити секцію ${sectionToEdit?.name}?`,
                        destructive: true,
                        confirmText: 'Видалити',
                      });
                      if (!result) {
                        return;
                      }

                      await galleryApi.deleteSection(selectedSection.id);
                      await loadSections();
                      setSelectedSection(null);
                      setSectionToEdit(null);
                    }}
                  >
                    Видалити
                  </Button>
                </div>
                {displayEditSection && (
                  <div>
                    <LanguageTabs
                      activeLanguage={activeLanguage}
                      onChange={setActiveLanguage}
                      idPrefix={languageTabPrefix}
                    />
                    <div
                      className="language-panel"
                      role="tabpanel"
                      id={getLanguagePanelId(languageTabPrefix, activeLanguage)}
                      aria-labelledby={getLanguageTabId(
                        languageTabPrefix,
                        activeLanguage
                      )}
                    >
                      <div className="language-fields">
                        <FormField label="Назва">
                          <Input
                            value={
                              activeLanguage === 'ua'
                                ? sectionToEdit?.name || ''
                                : activeLanguage === 'en'
                                  ? sectionToEdit?.nameEn || ''
                                  : sectionToEdit?.nameDe || ''
                            }
                            onChange={newValue => {
                              setSectionToEdit(prevValue => {
                                if (!prevValue) {
                                  return prevValue;
                                }
                                return {
                                  ...prevValue,
                                  ...(activeLanguage === 'ua'
                                    ? { name: newValue }
                                    : activeLanguage === 'en'
                                      ? { nameEn: newValue }
                                      : { nameDe: newValue }),
                                };
                              });
                            }}
                          />
                        </FormField>
                      </div>
                    </div>
                    <Checkbox
                      checked={sectionToEdit?.active}
                      onChange={(_value, checked) => {
                        setSectionToEdit(prevValue => ({
                          ...prevValue,
                          active: checked,
                        }));
                      }}
                    >
                      Відображати на сайті
                    </Checkbox>

                    <Button
                      onClick={async () => {
                        if (
                          !sectionToEdit ||
                          !sectionToEdit.id ||
                          !sectionToEdit.name ||
                          !sectionToEdit.nameEn ||
                          !sectionToEdit.nameDe
                        ) {
                          return;
                        }

                        await galleryApi.updateSection(
                          sectionToEdit.id,
                          sectionToEdit.name,
                          sectionToEdit.nameEn,
                          sectionToEdit.nameDe,
                          sectionToEdit.active || false
                        );
                        await loadSections();
                      }}
                      disabled={
                        JSON.stringify(sectionToEdit) ===
                        JSON.stringify(
                          sections.find(s => s.id === selectedSection?.id)
                        )
                      }
                    >
                      Зберегти
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {selectedSection && (
            <UploadImages
              section={selectedSection}
              refreshImages={async () => {
                getData()
                  .catch(console.error)
                  .then(() => setLoading(false));
              }}
            />
          )}
          {selectedSection && (
            <>
              <Table
                loading={loading}
                autoHeight={true}
                affixHeader={true}
                affixHorizontalScrollbar={true}
                bordered={true}
                rowHeight={300}
                renderLoading={renderTableLoading}
                renderEmpty={renderTableEmpty}
                data={state.data}
              >
                <Column fixed="left" width={180}>
                  <HeaderCell>Зображення</HeaderCell>
                  <Cell>
                    {rowData => (
                      <div>
                        <img
                          style={{
                            objectFit: 'contain',
                            width: '100%',
                            height: '100%',
                          }}
                          src={rowData.image}
                        />
                      </div>
                    )}
                  </Cell>
                </Column>

                <Column width={148}>
                  <HeaderCell>Дата завантаження</HeaderCell>
                  <Cell dataKey="createdAt">
                    {rowData => new Date(rowData.createdAt).toLocaleString()}
                  </Cell>
                </Column>

                <Column fixed="right" width={120}>
                  <HeaderCell>Дії</HeaderCell>
                  <Cell
                    onClick={() => {
                      clickedCellRef.current = true;
                    }}
                    style={{ padding: '6px' }}
                  >
                    {rowData => (
                      <>
                        <Button
                          appearance="subtle"
                          onClick={async () => {
                            const result = await confirm({
                              message: 'Ви впевнені, що хочете видалити фото?',
                              destructive: true,
                              confirmText: 'Видалити',
                            });
                            if (!result) {
                              return;
                            }
                            try {
                              await galleryApi.deleteSectionImage(
                                selectedSection.id,
                                rowData.id
                              );
                              toaster.push(
                                <Message type="success" showIcon closable>
                                  Фото видалено
                                </Message>,
                                {
                                  placement: 'topEnd',
                                  duration: 1500,
                                }
                              );
                              getData(state.page)
                                .catch(console.error)
                                .then(() => setLoading(false));
                            } catch (err: any) {
                              console.error(err);
                              const errorMessage =
                                err.response?.data?.message ||
                                'Не вдалося видалити фото. Спробуйте пізніше';
                              toaster.push(
                                <Message type="error" showIcon closable>
                                  {errorMessage}
                                </Message>,
                                {
                                  placement: 'topEnd',
                                  duration: 1500,
                                }
                              );
                            }
                          }}
                        >
                          <DeleteIcon />
                        </Button>
                      </>
                    )}
                  </Cell>
                </Column>
              </Table>

              <TablePagination
                total={state.totalCount}
                activePage={state.page}
                pages={state.totalPages}
                limit={perPage}
                limitOptions={[15, 30, 50, 100]}
                onChangeLimit={setPerPage}
                onChangePage={getData}
              />
            </>
          )}

          {createModalOpen && (
            <CreateSectionModal
              open={createModalOpen}
              setOpen={setCreateModalOpen}
              callback={loadSections}
            />
          )}
        </div>
      </PageLayout>
    </>
  );
}
